/**
 * Runtime configuration — the single typed accessor for app config.
 *
 * Production: the web container substitutes values into `window.__ENV__` at
 * start (docker-entrypoint → env.js), so config is runtime-, not build-, bound.
 * Dev: falls back to Vite `VITE_*` vars. Unsubstituted placeholders (`%VAR%`)
 * are treated as absent.
 */
declare global {
  interface Window {
    __ENV__?: Record<string, string>;
  }
}

function read(key: string): string {
  const runtime =
    typeof window !== "undefined" ? window.__ENV__?.[key] : undefined;
  if (runtime && !runtime.startsWith("%")) return runtime;
  const fromVite = (import.meta.env as Record<string, string | undefined>)[
    `VITE_${key}`
  ];
  return fromVite ?? "";
}

export const env = {
  BROWSER_TELEMETRY_URL: read("BROWSER_TELEMETRY_URL"),
  API_BASE_URL: read("API_BASE_URL") || "https://api.anyaistudio.com",
  // Local-only authentication bypass. Pair with backend DEV_AUTH_BYPASS=1 so requests
  // fail closed unless both sides explicitly opt into synthetic authentication.
  AUTH_BYPASS:
    import.meta.env.DEV &&
    (read("AUTH_BYPASS") === "1" || read("AUTH_BYPASS") === "true"),
  // Explicit local-only visual preview for generation workspaces. The Vite
  // production build resolves the fixture module to null as a second guard.
  GENERATION_VISUAL_PREVIEW:
    import.meta.env.DEV &&
    import.meta.env.MODE === "development" &&
    (read("GENERATION_VISUAL_PREVIEW") === "1" ||
      read("GENERATION_VISUAL_PREVIEW") === "true"),
  // Consent-gated product analytics. Both values are required so a partial
  // deployment fails closed; use the root domain to share consent and visitor
  // attribution between the landing site and app subdomain.
  DATAFAST_WEBSITE_ID: read("DATAFAST_WEBSITE_ID"),
  DATAFAST_DOMAIN: read("DATAFAST_DOMAIN"),
  DATAFAST_ALLOW_LOCALHOST:
    import.meta.env.DEV &&
    (read("DATAFAST_ALLOW_LOCALHOST") === "1" ||
      read("DATAFAST_ALLOW_LOCALHOST") === "true"),
  // Public landing origin used for privacy controls. A missing value falls
  // back to same-origin instead of silently sending local/staging users to prod.
  LANDING_BASE_URL: read("LANDING_BASE_URL"),
} as const;
